// Stars — instanced starfield of small tinted points in a sphere of
// radius 30. Uses InstancedMesh with PlaneGeometry(0.04, 0.04) and
// MeshBasicMaterial vertex colors. Per-instance position, slight per-instance
// scale variation. Subtle blink: opacity 0.4 -> 0.7 -> 0.4 on a 4s
// cycle, randomized phase per instance.
//
// No interaction. Always-on so the scene never feels empty.

import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { InstancedMesh, Object3D } from 'three';

const STAR_COUNT = 950;
const STAR_RADIUS = 30;
const STAR_PLANE_SIZE = 0.04;
const BLINK_CYCLE_S = 4;
const STAR_PALETTE = ['#ffffff', '#efe8dc', '#b9dec9', '#c6ad78', '#b9c7ff'];

// Deterministic-ish RNG so the field is stable across renders / reloads.
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function Stars() {
  const meshRef = useRef<InstancedMesh | null>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  // Per-instance data: base scale, blink phase, tint.
  const data = useMemo(() => {
    const rand = mulberry32(0x57a25);
    const items: { phase: number; scale: number; pos: THREE.Vector3; color: THREE.Color }[] = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      // Uniform on a sphere.
      const u = rand();
      const v = rand();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = STAR_RADIUS * (0.85 + rand() * 0.3); // slight depth jitter
      const pos = new THREE.Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi),
      );
      const paletteIndex = Math.floor(rand() * STAR_PALETTE.length);
      const color = new THREE.Color(STAR_PALETTE[paletteIndex]);
      color.lerp(new THREE.Color('#ffffff'), rand() * 0.24);
      items.push({
        pos,
        phase: rand() * BLINK_CYCLE_S,
        scale: 0.55 + rand() * 0.9, // 0.55..1.45
        color,
      });
    }
    return items;
  }, []);

  // Initial placement.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    for (let i = 0; i < STAR_COUNT; i++) {
      const d = data[i];
      dummy.position.copy(d.pos);
      dummy.scale.setScalar(d.scale);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, d.color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [data, dummy]);

  // Per-frame: nothing to update for matrix (we don't move them). The
  // blink is faked via material opacity oscillation — but since opacity
  // is a uniform, all stars blink in sync. We approximate "randomized
  // phase" by slightly varying the size sinusoidally per-star via a
  // tiny per-instance scale wobble. Cheap, looks alive.
  useFrame((state) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = state.clock.elapsedTime;
    // Update only a slice each frame to amortize cost.
    // With 800 instances it's still trivial; we just do all of them.
    for (let i = 0; i < STAR_COUNT; i++) {
      const d = data[i];
      const phase = d.phase;
      // 0..1 -> 0.4..0.7..0.4 (peak at half cycle)
      const k = (t + phase) % BLINK_CYCLE_S;
      const blink = 0.55 + 0.15 * Math.sin((k / BLINK_CYCLE_S) * Math.PI * 2);
      const s = d.scale * blink;
      dummy.position.copy(d.pos);
      dummy.scale.setScalar(s);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, STAR_COUNT]}
      name="stars"
      frustumCulled={false}
      raycast={() => null}
    >
      <planeGeometry args={[STAR_PLANE_SIZE, STAR_PLANE_SIZE]} />
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={0.62}
        depthWrite={false}
      />
    </instancedMesh>
  );
}

export default Stars;
